import { useMutation, gql, useApolloClient } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import styled from 'styled-components'

import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { SpinnerIcon } from '@10xLabs/react-web/components/Icons/Spinner'
import { VEHICLE_COMPONENT } from 'features/VehicleTemplates/components/Components/constants'
import { ComponentIcon } from 'features/VehicleTemplates/components/Components/Icon'
import { resolvers as seatsResolvers } from 'resolvers/seats'
import { isConflictError } from 'utils/error'
import { device } from 'utils/style'
import { ICellProps, INewTicketData, ISeatLabelProps } from './types'
import { SEAT_STATUS } from '../SeatStatus/constants'

const Container = styled.div<{ isCorridor?: boolean }>`
  width: ${(props): string => (props.isCorridor ? '20px' : '45px')};
  height: 40px;
  margin: 4px;

  @media ${device.laptop} {
    flex-direction: column-reverse;
    margin: 2px 1px;
    width: 35px;
    height: ${(props): string => (props.isCorridor ? '35px' : '55px')};
  }
`

const IconWrapper = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  svg {
    fill: #9999bd;
  }
`

const DriverWrapper = styled(IconWrapper)<TSFixMe>`
  @media ${device.laptop} {
    transform: rotate(-90deg);
  }
`

const SeatLabelContainer = styled.div<{ isOccupied?: boolean; isSelected?: boolean }>`
  cursor: ${(props): string => (props.isOccupied ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props): string => (props.isOccupied ? '#e5e5e5' : props.isSelected ? '#1976d2' : '#ffffff')};
  color: ${(props): string => (props.isOccupied ? '#6b6b6b' : props.isSelected ? '#ffffff' : '#1976d2')};
  border: ${(props): string => (props.isOccupied || props.isSelected ? 'none' : '1.5px solid #1976d2')};
  width: ${(props): string => (props.isOccupied || props.isSelected ? '100%' : 'calc(100% - 3px)')};
  height: ${(props): string => (props.isOccupied || props.isSelected ? '100%' : 'calc(100% - 3px)')};
  border-radius: 2px;
`
const StandarSeatSpinnerWrapper = styled.div`
  display: flex;

  svg {
    fill: #06a394;
  }
`
const PreferredSeatSpinnerWrapper = styled(StandarSeatSpinnerWrapper)`
  svg {
    fill: #c067ff;
  }
`
const PreferredSeatLabel = styled(SeatLabelContainer)`
  color: ${(props): string => (props.isOccupied ? '#6b6b6b' : props.isSelected ? '#ffffff' : '#C067FF')};
  border: ${(props): string => (props.isOccupied || props.isSelected ? 'none' : '1.5px solid #C067FF')};
`

const SeatLabelContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > span {
    font-size: 12px;
    line-height: 18px;
  }

  & > div {
    font-size: 8px;
    line-height: 12px;
  }
`
const CHANGE_TICKET_SEAT = gql`
  mutation ChangeTicketSeat($bookingId: ID!, $tripChangeId: ID!, $ticketId: ID!, $seatId: ID!) {
    changeBookingTripChangeTicketSeat(
      input: { id: $bookingId, tripChangeID: $tripChangeId, ticketID: $ticketId, seatID: $seatId }
    )
  }
`

const UPDATE_SEAT_STATUS = gql`
  mutation UpdateSeatStatus($seatId: ID!, $status: Status!) {
    updateSeatStatus(seatId: $seatId, status: $status) @client
  }
`

const SeatLabel = ({
  isOccupied,
  isSelected,
  isPreferredSeat,
  loading,
  label,
  onClick,
  price
}: ISeatLabelProps): ReactElement => {
  const Container = isPreferredSeat ? PreferredSeatLabel : SeatLabelContainer
  const SpinnerContainer = isPreferredSeat ? PreferredSeatSpinnerWrapper : StandarSeatSpinnerWrapper
  return (
    <Container isOccupied={isOccupied} isSelected={isSelected} onClick={onClick}>
      {loading ? (
        <SpinnerContainer>
          <SpinnerIcon />
        </SpinnerContainer>
      ) : (
        <SeatLabelContent>
          <span>{label}</span>
          {isPreferredSeat && !isOccupied && (
            <div>
              <span>{'+'}</span>
              <FormattedPrice span value={price} showCurrency={false} />
            </div>
          )}
        </SeatLabelContent>
      )}
    </Container>
  )
}

export const Cell = (props: ICellProps): ReactElement => {
  const client = useApolloClient()
  client.addResolvers(seatsResolvers)

  const {
    component,
    isCorridor,
    label,
    seat,
    ticket,
    oldTickets,
    nextTicket,
    booking,
    showUnavailableSeatError,
    showSeatError,
    onStartToReserveSeat,
    tripSegment,
    canReserveSeat,
    isModifyingSameTripSegment,
    ...formProps
  } = props
  let content
  const isSelected = !isEmpty(ticket)
  const isPreferredSeat = (seat?.preferredSeatPrice ?? 0) > 0
  const isSelectable =
    seat &&
    !isEmpty(oldTickets.find((oldTicket): TSFixMe => oldTicket?.seat?.id === seat.id)) &&
    isModifyingSameTripSegment
  const isOccupied = seat && seat.status === SEAT_STATUS.OCCUPIED && !isSelectable

  const updateTickets = (): void => {
    let newTickets = []
    const newTicketData: INewTicketData = {
      seat,
      trip: {
        id: tripSegment?.tripID
      }
    }

    if (oldTickets.length > formProps.values.tickets.length) {
      newTickets = [
        ...formProps.values.tickets,
        {
          ...nextTicket,
          ...newTicketData,
          updatedCount: 1
        }
      ]
    } else {
      newTickets = formProps.values.tickets.map((ticket): TSFixMe => {
        if (ticket.id === nextTicket.id) {
          return {
            ...ticket,
            ...newTicketData,
            updatedCount: (ticket.updatedCount ?? 0) + 1
          }
        }

        return ticket
      })
    }

    formProps.setFieldValue('tickets', newTickets)
  }

  const [updateSeatStatus] = useMutation(UPDATE_SEAT_STATUS)
  const [changeSeat, { loading }] = useMutation(CHANGE_TICKET_SEAT, {
    onCompleted() {
      formProps.setLoading(false)
      if (formProps.values.tickets.length === 0) {
        onStartToReserveSeat()
      }
      updateTickets()
    },
    onError(error) {
      formProps.setLoading(false)
      if (isConflictError(error)) {
        showUnavailableSeatError(true)
        updateSeatStatus({
          variables: {
            seatId: seat.id,
            status: SEAT_STATUS.OCCUPIED
          }
        })
      } else {
        showSeatError(true)
      }
    }
  })

  const handleClick = (): void => {
    if (!isOccupied && !isSelected && !loading && !formProps.loading) {
      if (canReserveSeat) {
        formProps.setLoading(true)
        changeSeat({
          variables: {
            bookingId: booking.id,
            tripChangeId: formProps.values.tripChangeId,
            ticketId: nextTicket.id,
            seatId: seat.id
          }
        })
      } else {
        updateTickets()
      }
    }
  }

  switch (component) {
    case VEHICLE_COMPONENT.DRIVER:
      content = (
        <DriverWrapper>
          <ComponentIcon component={component} />
        </DriverWrapper>
      )
      break
    case VEHICLE_COMPONENT.DOOR:
    case VEHICLE_COMPONENT.STAIRCASE:
    case VEHICLE_COMPONENT.WC:
      content = (
        <IconWrapper>
          <ComponentIcon component={component} />
        </IconWrapper>
      )
      break
    case VEHICLE_COMPONENT.SEAT:
      content = (
        <SeatLabel
          isOccupied={isOccupied}
          isSelected={isSelected}
          onClick={handleClick}
          loading={loading}
          isPreferredSeat={isPreferredSeat}
          label={label}
          price={seat.preferredSeatPrice}
        />
      )
      break
  }

  return <Container isCorridor={isCorridor}>{content}</Container>
}
